import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { Building2, CheckCircle2, ClipboardList, HardHat, Users } from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { Btn, Chyba, Input, Pole } from '../components/ui.js';
import { sledujUdalost } from '../lib/analytika.js';

/**
 * Registrace — samoobslužné založení firmy. Levý panel říká, co bude následovat
 * (tři kroky onboardingu), pravý je formulář: firma (IČO doplní zbytek z ARES)
 * + první uživatel (ředitel). Po úspěchu rovnou do /vitejte.
 */
export function RegistracePage() {
  const { me, registrace } = useAuth();
  const [firmaNazev, setFirmaNazev] = useState('');
  const [ico, setIco] = useState('');
  const [dic, setDic] = useState<string | null>(null);
  const [adresa, setAdresa] = useState<string | null>(null);
  const [aresStav, setAresStav] = useState<'klid' | 'hledam' | 'nalezeno' | 'chyba'>('klid');
  const [aresHlaska, setAresHlaska] = useState<string | null>(null);
  const [jmeno, setJmeno] = useState('');
  const [email, setEmail] = useState('');
  const [heslo, setHeslo] = useState('');
  const [zobrazitHeslo, setZobrazitHeslo] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  const [odesilam, setOdesilam] = useState(false);
  const [hotovo, setHotovo] = useState(false);

  // Deklarativně, ne imperativním navigate(): router v7 balí navigate do
  // transition a urgentní setState by guard níže stihl přesměrovat na „/"
  // dřív — čerstvá firma by prolétla na Přehled bez uvítání.
  if (hotovo) return <Navigate to="/vitejte" replace />;
  if (me && !odesilam) return <Navigate to="/" replace />;

  const hesloOk = heslo.length >= 8 && /[A-Za-zÀ-ž]/.test(heslo) && /\d/.test(heslo);

  const doplnZAres = async (icoCiste: string) => {
    setAresStav('hledam');
    setAresHlaska(null);
    try {
      const data = await api.get<{
        nazev: string | null;
        dic: string | null;
        adresa: string | null;
      }>(`/api/registrace/ares/${icoCiste}`);
      if (data.nazev) setFirmaNazev(data.nazev);
      setDic(data.dic);
      setAdresa(data.adresa);
      setAresStav('nalezeno');
    } catch (err) {
      setAresStav('chyba');
      setAresHlaska(hlaskaZChyby(err));
    }
  };

  const odeslat = async (e: FormEvent) => {
    e.preventDefault();
    setChyba(null);
    setOdesilam(true);
    try {
      await registrace({
        firmaNazev,
        ico: ico.trim() || null,
        dic,
        adresa,
        jmeno,
        email,
        heslo,
      });
      sledujUdalost('registrace_dokoncena');
      setHotovo(true);
    } catch (err) {
      setChyba(hlaskaZChyby(err));
    } finally {
      setOdesilam(false);
    }
  };

  return (
    <main className="min-h-screen bg-bg lg:grid lg:grid-cols-[1.1fr_1fr]">
      {/* Co bude následovat — registrace není skok do prázdna. */}
      <section className="flex flex-col justify-between gap-10 bg-primary p-6 text-white lg:min-h-screen lg:p-12">
        <div className="flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-text-heading">
            <HardHat size={20} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="text-lg font-bold tracking-widest uppercase">{APP_NAME}</span>
        </div>

        <div className="max-w-xl">
          <h1 className="text-3xl leading-tight font-bold text-balance text-white lg:text-4xl">
            Za dvě minuty máte firmu v Helfru.
          </h1>
          <p className="mt-3 max-w-prose text-lg text-primary-tint">
            Žádná instalace, žádná platební karta. Založíte účet a provedeme vás prvními kroky:
          </p>
          <ol className="mt-8 hidden flex-col gap-5 lg:flex">
            <KrokNahled
              ikona={<Building2 size={20} strokeWidth={1.75} />}
              cislo="1"
              titulek="Firma a účet"
            >
              Název firmy stačí — IČO doplní zbytek z rejstříku samo.
            </KrokNahled>
            <KrokNahled
              ikona={<ClipboardList size={20} strokeWidth={1.75} />}
              cislo="2"
              titulek="První zakázka"
            >
              Číslo a název stavby. Deník, výkresy a vícepráce se na ni rovnou zavěsí.
            </KrokNahled>
            <KrokNahled ikona={<Users size={20} strokeWidth={1.75} />} cislo="3" titulek="Váš tým">
              Pozvete mistra a stavbyvedoucí — každý uvidí jen to, co mu patří.
            </KrokNahled>
          </ol>
        </div>

        <p className="text-sm text-primary-tint">
          Máte už účet?{' '}
          <Link to="/login" className="font-semibold text-white underline underline-offset-2">
            Přihlaste se
          </Link>
        </p>
      </section>

      {/* Formulář registrace. */}
      <section className="flex flex-col items-center justify-center gap-4 p-6 lg:p-12">
        <form
          onSubmit={(e) => void odeslat(e)}
          className="flex w-full max-w-md flex-col gap-6 rounded-xl border border-border bg-surface p-8 shadow-md"
        >
          <div>
            <h2 className="text-2xl font-bold">Založit firmu</h2>
            <p className="mt-1 text-base text-text-secondary">
              Vy budete ředitel — role a tým doplníte hned potom.
            </p>
          </div>

          <fieldset className="flex flex-col gap-4">
            <legend className="mb-2 text-xs font-semibold tracking-wider text-text-secondary uppercase">
              Firma
            </legend>
            <Pole popisek="IČO" napoveda="8 číslic — zbytek doplníme z ARES">
              <div className="flex gap-2">
                <Input
                  name="ico"
                  inputMode="numeric"
                  maxLength={8}
                  value={ico}
                  onChange={(e) => {
                    const ciste = e.target.value.replace(/\D/g, '');
                    setIco(ciste);
                    setAresStav('klid');
                    if (ciste.length === 8) void doplnZAres(ciste);
                  }}
                />
                {aresStav === 'hledam' && (
                  <span className="flex items-center text-sm whitespace-nowrap text-text-secondary">
                    Hledám…
                  </span>
                )}
                {aresStav === 'nalezeno' && (
                  <span className="flex items-center gap-1 text-sm whitespace-nowrap text-success-text">
                    <CheckCircle2 size={16} strokeWidth={2} aria-hidden="true" /> Doplněno
                  </span>
                )}
              </div>
            </Pole>
            {aresStav === 'chyba' && aresHlaska && (
              <p className="text-sm text-warning-text">{aresHlaska}</p>
            )}
            <Pole popisek="Název firmy" povinne>
              <Input
                name="firmaNazev"
                required
                value={firmaNazev}
                onChange={(e) => setFirmaNazev(e.target.value)}
              />
            </Pole>
            {adresa && <p className="text-sm text-text-secondary">{adresa}</p>}
          </fieldset>

          <fieldset className="flex flex-col gap-4">
            <legend className="mb-2 text-xs font-semibold tracking-wider text-text-secondary uppercase">
              Váš účet
            </legend>
            <Pole popisek="Jméno a příjmení" povinne>
              <Input
                name="jmeno"
                required
                value={jmeno}
                onChange={(e) => setJmeno(e.target.value)}
              />
            </Pole>
            <Pole popisek="E-mail" povinne>
              <Input
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Pole>
            <Pole
              popisek="Heslo"
              povinne
              napoveda={hesloOk ? undefined : 'aspoň 8 znaků, písmeno a číslice'}
            >
              <div className="relative">
                <Input
                  type={zobrazitHeslo ? 'text' : 'password'}
                  name="heslo"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  className="pr-24"
                  value={heslo}
                  onChange={(e) => setHeslo(e.target.value)}
                />
                <button
                  type="button"
                  aria-pressed={zobrazitHeslo}
                  onClick={() => setZobrazitHeslo((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-sm font-semibold text-primary hover:bg-primary-tint"
                >
                  {zobrazitHeslo ? 'Skrýt' : 'Zobrazit'}
                </button>
              </div>
              {hesloOk && (
                <p className="mt-1 flex items-center gap-1 text-sm text-success-text">
                  <CheckCircle2 size={14} strokeWidth={2} aria-hidden="true" /> Heslo je v pořádku.
                </p>
              )}
            </Pole>
          </fieldset>

          <Chyba>{chyba}</Chyba>
          <Btn type="submit" className="w-full" disabled={odesilam}>
            {odesilam ? 'Zakládám firmu…' : 'Založit firmu a začít'}
          </Btn>
          <p className="text-center text-sm text-text-secondary lg:hidden">
            Máte už účet?{' '}
            <Link to="/login" className="font-semibold text-primary underline underline-offset-2">
              Přihlaste se
            </Link>
          </p>
        </form>
      </section>
    </main>
  );
}

/** Náhled kroku onboardingu na navy panelu (číslo + ikona + věta). */
function KrokNahled({
  ikona,
  cislo,
  titulek,
  children,
}: {
  ikona: React.ReactNode;
  cislo: string;
  titulek: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-accent">
        {ikona}
      </span>
      <span className="min-w-0">
        <span className="block text-base font-bold">
          {cislo}. {titulek}
        </span>
        <span className="block max-w-prose text-base text-primary-tint">{children}</span>
      </span>
    </li>
  );
}
